/**
 * The route table. One route per screen; the signed-in area is a nested
 * layout (`AppShell`) behind `RequireAuth`.
 */
import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ROUTES } from './routes';
import { ErrorBoundary } from './ErrorBoundary';
import { RequireAuth } from '../auth/RequireAuth';
import { FullPageLoader } from '../components/FullPageLoader';
import { LandingPage } from '../features/landing/LandingPage';
import { initAnalytics, pageview, track } from '../lib/analytics';
import { legal } from '../lib/legal';
import { isAndroidApp } from '../lib/platform';
import { whenIdle } from '../lib/idle';

// Matches `/app/studio` and `/app/studio/:resumeId`, not the `/complete`
// route (that one's own automatic `$pageview` is event 7 — see
// .scratch/product-analytics/issues/06-resume-studio-funnel.md).
const STUDIO_ROUTE_RE = /^\/app\/studio(\/[^/]+)?$/;

// The landing page is the web's front door and stays in the entry chunk. The funnel, sign-in and the Android
// sign-in return are chunks of their own (faster-screens 02): the app opens at /app and never needed them.
const loadFunnel = () => import('../features/funnel/FunnelPage');
const loadAuth = () => import('../features/auth/AuthPage');
const loadAppReturn = () => import('../features/auth/AppReturnPage');
const FunnelPage = lazy(() => loadFunnel().then((m) => ({ default: m.FunnelPage })));
const AuthPage = lazy(() => loadAuth().then((m) => ({ default: m.AuthPage })));
const AppReturnPage = lazy(() => loadAppReturn().then((m) => ({ default: m.AppReturnPage })));

// The dashboard (and the resume editor with its PDF pipeline) only loads once someone signs in.
const loadShell = () => import('../features/shell/AppShell');
const loadInsights = () => import('../features/insights/InsightsScreen');
const loadSearch = () => import('../features/search/SearchScreen');
const loadJobDetail = () => import('../features/search/JobDetailScreen');
const loadDeck = () => import('../features/deck/DeckScreen');
const loadAction = () => import('../features/action/ActionScreen');
const loadTracking = () => import('../features/tracking/TrackingScreen');
const loadStudio = () => import('../features/studio/StudioScreen');
const AppShell = lazy(() => loadShell().then((m) => ({ default: m.AppShell })));
const InsightsScreen = lazy(() => loadInsights().then((m) => ({ default: m.InsightsScreen })));
const SearchScreen = lazy(() => loadSearch().then((m) => ({ default: m.SearchScreen })));
const JobDetailScreen = lazy(() => loadJobDetail().then((m) => ({ default: m.JobDetailScreen })));
const DeckScreen = lazy(() => loadDeck().then((m) => ({ default: m.DeckScreen })));
const ActionScreen = lazy(() => loadAction().then((m) => ({ default: m.ActionScreen })));
const TrackingScreen = lazy(() => loadTracking().then((m) => ({ default: m.TrackingScreen })));
const ResumeHealthScreen = lazy(() => import('../features/resumeHealth/ResumeHealthScreen').then((m) => ({ default: m.ResumeHealthScreen })));
const StudioScreen = lazy(() => loadStudio().then((m) => ({ default: m.StudioScreen })));
const TailorHubScreen = lazy(() => import('../features/tailor/TailorHubScreen').then((m) => ({ default: m.TailorHubScreen })));
const RebuildScreen = lazy(() => import('../features/tailor/RebuildScreen').then((m) => ({ default: m.RebuildScreen })));
const GapQuestionsScreen = lazy(() => import('../features/tailor/GapQuestionsScreen').then((m) => ({ default: m.GapQuestionsScreen })));
const RebuildProgressScreen = lazy(() => import('../features/tailor/RebuildProgressScreen').then((m) => ({ default: m.RebuildProgressScreen })));
const TailorPageScreen = lazy(() => import('../features/tailor/TailorPageScreen').then((m) => ({ default: m.TailorPageScreen })));
const AutoApplyScreen = lazy(() => import('../features/auto/AutoApplyScreen').then((m) => ({ default: m.AutoApplyScreen })));
const BillingScreen = lazy(() => import('../features/billing/BillingScreen').then((m) => ({ default: m.BillingScreen })));
const UsageScreen = lazy(() => import('../features/billing/UsageScreen').then((m) => ({ default: m.UsageScreen })));
const AccountScreen = lazy(() => import('../features/account/AccountScreen').then((m) => ({ default: m.AccountScreen })));
const SkillBankPage = lazy(() => import('../features/skillBank/SkillBankPage').then((m) => ({ default: m.SkillBankPage })));
const ProfilePage = lazy(() => import('../features/account/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const AccountLayout = lazy(() => import('../features/account/AccountLayout').then((m) => ({ default: m.AccountLayout })));
const AccountIndex = lazy(() => import('../features/account/AccountLayout').then((m) => ({ default: m.AccountIndex })));
const MorePage = lazy(() => import('../features/shell/MorePage').then((m) => ({ default: m.MorePage })));
const JobPreferencesPage = lazy(() => import('../features/account/JobPreferencesPage').then((m) => ({ default: m.JobPreferencesPage })));
const AnswersPage = lazy(() => import('../features/account/AnswersPage').then((m) => ({ default: m.AnswersPage })));
const PrintResumeScreen = lazy(() => import('../features/print/PrintResume').then((m) => ({ default: m.PrintResumeScreen })));
// The Android app's debug galleries (android-polish tickets 03 and 04); routed only in the app, so the web keeps its fallback.
const AndroidGallery = lazy(() => import('../android/Gallery').then((m) => ({ default: m.AndroidGallery })));
const PrintPreviewScreen = lazy(() => import('../features/print/PrintPreview').then((m) => ({ default: m.PrintPreviewScreen })));
// The legal documents' text is long and rarely read: it loads with its own pages only.
const LegalPages = lazy(() => import('../features/legal/LegalPages').then((m) => ({ default: m.LegalPages })));

/**
 * Starts the chunks the first screen will ask for, before the first render (faster-screens 02). Without it an app
 * start waits for the session, then fetches the frame, then the frame asks for the screen: three steps in a row.
 * The same loaders the routes use, so the browser fetches each chunk once. A public page fetches the funnel and
 * sign-in once it is idle, so the step from the landing page into either does not wait.
 */
export function preloadFor(pathname: string): void {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/app' || path.startsWith('/app/')) {
    void loadShell();
    const screen =
      path === '/app' || path.startsWith(ROUTES.shortlist) ? loadDeck
      : path === ROUTES.action ? loadAction
      : path === ROUTES.search ? loadSearch
      : path.startsWith(ROUTES.search + '/') ? loadJobDetail
      : path === ROUTES.insights ? loadInsights
      : path === ROUTES.tracking ? loadTracking
      : path.startsWith(ROUTES.studio) ? loadStudio
      : null;
    if (screen) void screen();
    return;
  }
  if (path === ROUTES.appReturn) { void loadAppReturn(); return; }
  if (path.startsWith('/print/')) return;
  whenIdle(() => { void loadFunnel(); void loadAuth(); });
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// React Router doesn't trigger a native page load, so PostHog's own
// autocapture never sees an SPA route change — this is what stands in for it.
// Also owns analytics init itself (`initAnalytics`), not a separate effect
// on `App`: on a fresh load React fires a child's mount effect before its
// parent's, so if init lived in `App` instead, this effect would still run
// first, see `initialized === false`, and silently drop the pageview (and,
// below, the studio check) — same failure shape either way. Calling
// `initAnalytics()` right here means init and everything that depends on
// it happen in one sequential effect, with no ordering to get wrong.
//
// Also owns `resume_studio_viewed` (ticket 06): `StudioScreen` backs all of
// `/app/studio`, `/app/studio/:resumeId` and `.../complete` as three
// separate `<Route>` entries, and React Router remounts it on every one of
// those transitions (confirmed by a real `scripts/flows.mjs` run — a
// mount-effect inside `StudioScreen` itself double-fired moving from the
// library into the editor). This component never remounts across any
// in-app navigation, so it's the one place that can tell "just entered the
// studio route family from outside it" from "moved between its own
// sub-routes" — the latter must not re-fire the event.
function AnalyticsPageview() {
  const { pathname } = useLocation();
  const prevPathname = React.useRef<string | null>(null);
  React.useEffect(() => {
    initAnalytics();
    pageview(pathname);
    const wasInStudio = prevPathname.current != null && STUDIO_ROUTE_RE.test(prevPathname.current);
    const isInStudio = STUDIO_ROUTE_RE.test(pathname);
    if (isInStudio && !wasInStudio) track('resume_studio_viewed');
    prevPathname.current = pathname;
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <>
      <ScrollToTop />
      <AnalyticsPageview />
      <ErrorBoundary>
      <Suspense fallback={<FullPageLoader label="Loading…" />}>
        <Routes>
          <Route path={ROUTES.landing} element={<LandingPage />} />
          {/* Funnel: every old intake/upload shape is an alias for /start (FunnelPage redirects). */}
          <Route path="/start/*" element={<FunnelPage />} />
          <Route path={ROUTES.report} element={<FunnelPage />} />
          <Route path="/templates" element={<FunnelPage />} />
          <Route path={ROUTES.auth} element={<AuthPage />} />
          <Route path={ROUTES.checkEmail} element={<AuthPage />} />
          {/* Android app ticket 08: the browser hands a sign-in back to the app. */}
          <Route path={ROUTES.appReturn} element={<AppReturnPage />} />
          {/* Ticket 37: the PDF export's print-only route. No RequireAuth —
              it has no session, and is authorized by the token in the URL. */}
          <Route path="/print/resume/:token" element={<PrintResumeScreen />} />
          {/* Ticket 38: the funnel before/after screenshot's print-only
              route — same reasoning, a different token namespace. */}
          <Route path="/print/preview/:token" element={<PrintPreviewScreen />} />
          {/* Legal pages (legal-pages ticket 01): public, signed in or out. Each
              document has a top-level path of its own (`/terms`, `/privacy`, …),
              and the hub owns every /policies path, so an unknown one there
              gets its own not-found state instead of the redirect to `/` below. */}
          <Route path={ROUTES.legal + '/*'} element={<LegalPages />} />
          {legal.documents.map((d) => (
            <Route key={d.slug} path={d.path} element={<LegalPages />} />
          ))}
          {/* A page that became a section (`/billing-terms`, now section 10 of
              the Terms): the old address, still in emails, leads to it. */}
          {legal.sections.filter((s) => s.to).map((s) => (
            <Route key={s.slug} path={s.path} element={<Navigate to={s.to} replace />} />
          ))}
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            {/* The home is today's shortlist (action-tab spec 1.1); Career Compass is locked for a new account (ADR 0007). */}
            <Route index element={<Navigate to={ROUTES.shortlist} replace />} />
            <Route path="insights" element={<InsightsScreen />} />
            <Route path="search" element={<SearchScreen />} />
            <Route path="search/:jobId" element={<JobDetailScreen />} />
            <Route path="shortlist" element={<DeckScreen />} />
            {/* The Ready tray's address (swipe-deck ticket 08): Action replaced the tray
                (action-tab ticket 15), and an old link or bookmark lands there. */}
            <Route path="shortlist/ready" element={<Navigate to={ROUTES.action} replace />} />
            <Route path="action" element={<ActionScreen />} />
            <Route path="tracking" element={<TrackingScreen />} />
            <Route path="resume-health" element={<ResumeHealthScreen />} />
            {/* The Skill bank is internal (skill-bank-internal-only ticket 06,
                reversing compass-health-rebuild ticket 07): its page left the
                product, and an old bookmark lands on home rather than a 404. */}
            <Route path="skills" element={<Navigate to={ROUTES.insights} replace />} />
            {/* The Probe questionnaire left the product (skill-bank-internal-only
                ticket 07): an old bookmark lands on home rather than a 404. */}
            <Route path="questionnaire" element={<Navigate to={ROUTES.insights} replace />} />
            <Route path="studio" element={<StudioScreen />} />
            <Route path="studio/:resumeId" element={<StudioScreen />} />
            <Route path="studio/:resumeId/complete" element={<StudioScreen />} />
            <Route path="resume-tailor" element={<TailorHubScreen />} />
            <Route path="rebuild" element={<RebuildScreen />} />
            <Route path="rebuild/:runId/questions" element={<GapQuestionsScreen />} />
            <Route path="rebuild/:runId/progress" element={<RebuildProgressScreen />} />
            <Route path="tailor" element={<TailorPageScreen />} />
            <Route path="auto" element={<AutoApplyScreen />} />
            <Route path="billing" element={<BillingScreen />} />
            <Route path="usage" element={<UsageScreen />} />
            {/* The Android app's More tab (android-app ticket 11); a plain list of links anywhere else. */}
            <Route path="more" element={<MorePage />} />
            {/* Account is a list of pages (ux-bug-pass-8 ticket 12): on a phone the list alone at /app/account, on
                desktop the list beside the open page, Profile when none is named. Every old path still lands. */}
            <Route path="account" element={<AccountLayout />}>
              <Route index element={<AccountIndex />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="profile/skills" element={<SkillBankPage />} />
              <Route path="preferences" element={<JobPreferencesPage />} />
              <Route path="security" element={<AccountScreen part="security" />} />
              <Route path="delete" element={<AccountScreen part="delete" />} />
              <Route path="answers" element={<AnswersPage />} />
            </Route>
            {isAndroidApp() ? <Route path="android/:page" element={<AndroidGallery />} /> : null}
            {/* The prototype's stray /app/jobs alias and any unknown /app path land on home, today's
                shortlist, as the index route does: not on the Career Compass, which is locked for a new account. */}
            <Route path="*" element={<Navigate to={ROUTES.shortlist} replace />} />
          </Route>
          <Route path="*" element={<Navigate to={ROUTES.landing} replace />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
}
